import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import CalculatorResult from 'src/components/CalculatorResult';
import { yupResolver } from '@hookform/resolvers/yup';
import { PaymentType } from 'src/utils/types';
import { requiredMoney, requiredPercentage } from 'src/utils/schema';
import Input from 'src/components/Input';
import { CalculateButton } from 'src/components/CalculateButton';
import { ResetButton } from 'src/components/ResetButton';
import { calculateYearsToPayout } from 'src/utils/annuity';
import { CalculateYearsToPayout, YearsToPayout as YearsToPayoutType } from 'src/utils/types';
import { ErrorMessage } from './types';

type Props = {
  customerName: string;
  paymentType: PaymentType;
  annualInflationRate: number;
  errorMessages: (p: ErrorMessage) => void;
};

type Form = CalculateYearsToPayout & {
  customerName: string;
};

const scheme = yup
  .object({
    firstAnnualPayment: requiredMoney,
    annualInterestRate: requiredPercentage,
    startPrincipal: requiredMoney,
    customerName: yup.string().required('This field is required'),
    PaymentType: yup.string(),
    annualInflationRate: requiredPercentage,
  })
  .required();

const YearsToPayout: React.FC<Props> = ({
  customerName,
  paymentType,
  annualInflationRate,
  errorMessages,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    resetField,
    setValue,
  } = useForm<Form>({
    resolver: yupResolver(scheme),
  });
  const [years, setYears] = React.useState<YearsToPayoutType>();

  const onSubmit = (data: Form) => {
    const res = calculateYearsToPayout(data);
    setYears(res);
  };

  const onResetClick = React.useCallback(() => {
    resetField('annualInterestRate');
    resetField('firstAnnualPayment');
    resetField('startPrincipal');
    setYears(undefined);
  }, [resetField]);

  React.useEffect(() => {
    errorMessages({
      customerName: errors.customerName?.message,
      annualInflationRate: errors.annualInflationRate?.message,
    });
  }, [errorMessages, errors.customerName?.message, errors.annualInflationRate?.message]);

  React.useEffect(() => {
    setValue('customerName', customerName);
  }, [customerName, setValue]);

  React.useEffect(() => {
    setValue('paymentType', paymentType);
  }, [paymentType, setValue]);

  React.useEffect(() => {
    setValue('annualInflationRate', annualInflationRate);
  }, [annualInflationRate, setValue]);

  return (
    <form className="mt-10" onSubmit={handleSubmit(onSubmit)}>
      <h3 className="text-lg leading-6 font-medium text-gray-900">Solve for Years To Payout</h3>
      <div className="mt-4 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
        <Input
          id="payoutStartingPrincipal"
          label="Starting Principal"
          register={register('startPrincipal')}
          errorMessage={errors.startPrincipal?.message}
          className="sm:col-span-2"
        />
        <Input
          id="payoutAnnualInterest"
          label="Annual Interest Rate (i)"
          register={register('annualInterestRate')}
          errorMessage={errors.annualInterestRate?.message}
          className="sm:col-span-2"
          trailingText="%"
        />
        <Input
          id="payoutFirstAnnualPayment"
          label="First Annual Payment"
          register={register('firstAnnualPayment')}
          errorMessage={errors.firstAnnualPayment?.message}
          className="sm:col-span-2"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 items-center justify-end">
        <CalculatorResult
          title="Years To Payout"
          fixedValue={years?.yearsToPayout ?? 0}
          inflationValue={years?.inflationAdjustedYearsToPayout ?? 0}
        />
        <div className="flex h-10 sm:col-start-2 md:col-start-4 justify-end">
          <ResetButton onClick={onResetClick} />
          <CalculateButton />
        </div>
      </div>
    </form>
  );
};

export default React.memo(YearsToPayout);
